import axios from 'axios';
import { BankDetails, PayoutRequest } from '../types/payout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const payoutService = {
  // Request a payout
  async requestPayout(starId: string, bankDetails: BankDetails): Promise<{
    success: boolean;
    message: string;
    payoutId?: string;
  }> {
    try {
      const response = await axios.post(`${API_URL}/api/payouts/request`, {
        starId,
        bankDetails,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to request payout',
      };
    }
  },

  // Get payout history for a star
  async getPayoutHistory(starId: string): Promise<PayoutRequest[]> {
    try {
      const response = await axios.get(`${API_URL}/api/payouts/history/${starId}`);
      
      // Convert Firestore timestamps to Date objects
      const payouts = response.data.payouts.map((payout: any) => ({
        ...payout,
        requestedAt: payout.requestedAt?._seconds 
          ? new Date(payout.requestedAt._seconds * 1000)
          : new Date(payout.requestedAt),
        completedAt: payout.completedAt?._seconds
          ? new Date(payout.completedAt._seconds * 1000)
          : payout.completedAt ? new Date(payout.completedAt) : undefined,
        claimedAt: payout.claimedAt?._seconds
          ? new Date(payout.claimedAt._seconds * 1000)
          : payout.claimedAt ? new Date(payout.claimedAt) : undefined,
        messages: payout.messages?.map((msg: any) => ({
          ...msg,
          timestamp: msg.timestamp?._seconds
            ? new Date(msg.timestamp._seconds * 1000)
            : new Date(msg.timestamp),
        })) || [],
      }));
      
      return payouts;
    } catch (error) {
      console.error('Error fetching payout history:', error);
      return [];
    }
  },

  // Add a message to a payout thread
  async addMessage(payoutId: string, starId: string, message: string): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await axios.post(`${API_URL}/api/payouts/${payoutId}/message`, {
        starId,
        message,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send message',
      };
    }
  },

  // Mark payout as claimed
  async markAsClaimed(payoutId: string, starId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await axios.post(`${API_URL}/api/payouts/${payoutId}/claim`, {
        starId,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to mark as claimed',
      };
    }
  },

  // Get payout configuration
  async getConfig(): Promise<{
    commissionRate: number;
    minPayoutAmount: number;
  }> {
    try {
      const response = await axios.get(`${API_URL}/api/payouts/config`);
      return response.data.config;
    } catch (error) {
      console.error('Error fetching payout config:', error);
      return {
        commissionRate: 20,
        minPayoutAmount: 100,
      };
    }
  },

  // Admin: Get all payouts
  async getAllPayouts(status?: 'open' | 'completed'): Promise<PayoutRequest[]> {
    try {
      const url = status 
        ? `${API_URL}/api/payouts/admin/all?status=${status}`
        : `${API_URL}/api/payouts/admin/all`;
      
      const response = await axios.get(url);
      
      // Convert Firestore timestamps to Date objects
      const payouts = response.data.payouts.map((payout: any) => ({
        ...payout,
        requestedAt: payout.requestedAt?._seconds 
          ? new Date(payout.requestedAt._seconds * 1000)
          : new Date(payout.requestedAt),
        completedAt: payout.completedAt?._seconds
          ? new Date(payout.completedAt._seconds * 1000)
          : payout.completedAt ? new Date(payout.completedAt) : undefined,
        claimedAt: payout.claimedAt?._seconds
          ? new Date(payout.claimedAt._seconds * 1000)
          : payout.claimedAt ? new Date(payout.claimedAt) : undefined,
        messages: payout.messages?.map((msg: any) => ({
          ...msg,
          timestamp: msg.timestamp?._seconds
            ? new Date(msg.timestamp._seconds * 1000)
            : new Date(msg.timestamp),
        })) || [],
      }));
      
      return payouts;
    } catch (error) {
      console.error('Error fetching all payouts:', error);
      return [];
    }
  },

  // Admin: Complete payout with proof
  async completePayout(payoutId: string, proof: {
    message?: string;
    imageUrl?: string;
  }): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await axios.post(
        `${API_URL}/api/payouts/admin/${payoutId}/complete`,
        { proof }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to complete payout',
      };
    }
  },

  // Admin: Add message
  async addAdminMessage(payoutId: string, message: string): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await axios.post(
        `${API_URL}/api/payouts/admin/${payoutId}/message`,
        { message }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send message',
      };
    }
  },
};

